<script setup lang="ts">
  /**
   * Record row group — 03-components/13c-record-row · OSCloud DS 1.1.170
   *
   * The list a `BfRecordRow` register sits in. Layout only.
   *
   * `wrap` gives it as many columns as the container has room for, driven by the minimum width
   * of a row rather than a column count — so 2-up in a form column, 1-up in a narrow rail and
   * 3-up on a wide page are the same declaration. A consumer whose rows need more room than
   * 260px raises `--bf-record-row-group-min-width`; it is a length, not a token, because it is
   * the measure of that page's content.
   */
  withDefaults(
    defineProps<{
      /** Renders as `<ol>` when the order of the rows is part of what they mean. */
      ordered?: boolean
      /** Fills the container's width with as many rows per line as fit. */
      wrap?: boolean
      /**
       * Rows lose their own box and become hairline-separated lines, for a register that sits
       * inside a box that already draws the edge (a Group container, a checklist).
       */
      flush?: boolean
    }>(),
    { ordered: false, wrap: false, flush: false },
  )
</script>

<template>
  <component
    :is="ordered ? 'ol' : 'ul'"
    class="record-row-group"
    :class="{ 'record-row-group--wrap': wrap, 'record-row-group--flush': flush }"
  >
    <slot />
  </component>
</template>

<style scoped lang="scss">
  .record-row-group {
    display: grid;
    gap: var(--bf-sp-2);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  /* auto-fit rather than auto-fill: one row in a wide container fills it instead of leaving an
     empty column beside itself. */
  .record-row-group--wrap {
    grid-template-columns: repeat(auto-fit, minmax(var(--bf-record-row-group-min-width, 260px), 1fr));
  }

  /* The rows are BfRecordRow roots, so they are reached through :deep(). */
  .record-row-group--flush {
    gap: 0;
  }

  .record-row-group--flush > :deep(.record-row) {
    padding: 14px 0;
    border: 0;
    border-bottom: 1px solid var(--bf-grey-light);
    border-radius: 0;
    background: transparent;
  }

  .record-row-group--flush > :deep(.record-row:last-child) {
    border-bottom: 0;
  }
</style>
